import { Mail, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';

type ComingSoonKind = 'classes' | 'creators' | 'locations' | 'updates';

const KIND_LABEL: Record<ComingSoonKind, string> = {
  classes: 'Classes',
  creators: 'Creators',
  locations: 'Locations',
  updates: 'Updates',
};

const KIND_BLURB: Record<ComingSoonKind, string> = {
  classes:
    'Local creators are setting up their first classes here. The listings below are examples of what’s coming.',
  creators:
    'We’re inviting local makers and instructors to build their pages here. The profiles below are examples of what’s coming.',
  locations:
    'We’re partnering with local studios and venues to host classes here. The spaces below are examples of what’s coming.',
  updates:
    'We’re gathering local news, events, and announcements to share here. Have something happening locally? Let us know.',
};

interface ComingSoonBannerProps {
  kind: ComingSoonKind;
  localityName: string;
  contactEmail?: string;
}

/**
 * Pinned "coming soon to <locality>" banner shown at the top of a browse list
 * for markets that haven't launched yet. Explains what's coming and gives
 * interested people a way to get in touch.
 */
export function ComingSoonBanner({
  kind,
  localityName,
  contactEmail = 'admin@kalovate.com',
}: ComingSoonBannerProps) {
  const label = KIND_LABEL[kind];
  const subject = encodeURIComponent(`Interested in ${label.toLowerCase()} in ${localityName}`);
  const body = encodeURIComponent(
    `Hi Kalovate team,\n\nI'd like to get involved with ${label.toLowerCase()} in ${localityName}.`
  );
  const mailto = `mailto:${contactEmail}?subject=${subject}&body=${body}`;

  return (
    <div className="rounded-[1.75rem] border border-dashed border-primary/40 bg-primary/5 p-6 shadow-sm">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
        <Sparkles className="h-3.5 w-3.5" />
        Coming soon
      </span>

      <h2 className="mt-3 text-2xl font-semibold">
        {label} are coming soon to {localityName}
      </h2>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">{KIND_BLURB[kind]}</p>

      <div className="mt-4">
        <a href={mailto}>
          <Button className="rounded-full px-5">
            <Mail className="mr-2 h-4 w-4" />
            Get involved in {localityName}
          </Button>
        </a>
        <p className="mt-2 text-xs text-muted-foreground">
          Interested in participating? Email{' '}
          <a className="underline" href={mailto}>
            {contactEmail}
          </a>
          .
        </p>
      </div>
    </div>
  );
}
